/*
  <aoa-search-drawer>: search drawer of the AoA design system (snippets/search-drawer.liquid).
  - Opens from any .thb-quick-search or [data-aoa-search-open] (delegated click, so header
    re-renders in the editor keep working) or from a native invoker (commandfor + show-modal).
    Those triggers are links to /search: without JS, or with a modifier key, they still go there.
  - Native modal <dialog> (showModal): top layer, Esc closes (cancel event), role dialog + aria-modal.
    On top of that: slide-in and veil, the rest of the page gets `inert` explicitly, Tab wraps inside
    the panel, scroll is locked on <html>, focus goes to the field and back to the trigger on close.
  - Quick view from a card closes the drawer first: the theme's product drawer lives outside the
    modal and would stay inert otherwise.
  - Predictive search: same request as the theme (section "predictive-search"), cancelled when a
    newer query starts; results replace the suggestions while the field has text.
  Emits on document: "aoa:search-open" (before opening, so other overlays can close),
  "search:open" (theme event, after opening) and "search:close".
*/
if (!customElements.get('aoa-search-drawer')) {
  const TRIGGERS = '.thb-quick-search, [data-aoa-search-open]';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]';
  const SKIP_INERT = /^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT|META)$/;
  const mobile = window.matchMedia('(max-width: 767px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));

  const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  // Longest transition of an element, in ms (durations drop to 0.01s with reduced motion)
  const transitionTime = (el) => {
    const style = getComputedStyle(el);
    const toMs = (value) => value.split(',').map((part) => parseFloat(part) * (part.includes('ms') ? 1 : 1000) || 0);
    const durations = toMs(style.transitionDuration);
    const delays = toMs(style.transitionDelay);
    return Math.max(0, ...durations.map((d, i) => d + (delays[i] || delays[0] || 0)));
  };

  const onTriggerClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const trigger = event.target.closest && event.target.closest(TRIGGERS);
    if (!trigger) return;
    const drawer = document.querySelector('aoa-search-drawer');
    if (!drawer || !drawer.ready || !drawer.styled()) return;
    event.preventDefault();
    drawer.open(trigger);
  };

  class AoaSearchDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.panel = this.querySelector('[data-aoa-search-panel]');
      this.veil = this.querySelector('[data-aoa-search-veil]');
      this.closeButton = this.querySelector('[data-aoa-search-close]');
      this.form = this.querySelector('[data-aoa-search-form]');
      this.input = this.querySelector('[data-aoa-search-input]');
      this.results = this.querySelector('[data-aoa-search-results]');
      this.initial = this.querySelector('[data-aoa-search-initial]');
      if (!this.panel || !this.input) return;

      this.ready = true;
      this.isOpen = false;
      this.active = false;
      this.inerted = [];

      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onNativeClose);
      this.dialog.addEventListener('keydown', this.onKeydown);
      this.dialog.addEventListener('focusin', this.onFocusin);
      this.dialog.addEventListener('command', this.onCommand);
      // Capture on the dialog itself: runs before the quick view handler of the card
      this.dialog.addEventListener('click', this.onHandoff, true);
      if (this.veil) this.veil.addEventListener('click', this.onDismiss);
      if (this.closeButton) this.closeButton.addEventListener('click', this.onDismiss);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      if (this.results) this.input.addEventListener('input', this.onInput);

      document.addEventListener('click', onTriggerClick);
      document.addEventListener('shopify:section:load', this.syncTriggers);
      this.syncTriggers();
    }

    disconnectedCallback() {
      if (!this.ready) return;
      this.ready = false;
      document.removeEventListener('click', onTriggerClick);
      document.removeEventListener('shopify:section:load', this.syncTriggers);
      if (this.controller) this.controller.abort();
      if (this.active) {
        this.isOpen = false;
        this.restoreFocus = false;
        this.finish();
      }
    }

    /* ---------- open and close ---------- */

    // The drawer CSS loads async: until it applies (the closed dialog computes to fixed only with it),
    // the trigger keeps its link to /search instead of opening an unstyled dialog
    styled() {
      return getComputedStyle(this.dialog).position === 'fixed';
    }

    open(trigger) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.opener = trigger || document.activeElement;
      clearTimeout(this.closeTimer);
      emit('aoa:search-open');

      if (!this.dialog.open) {
        this.dialog.removeAttribute('data-state');
        this.dialog.showModal();
        // The initial focus of showModal() may scroll the layer to the off-screen field (no overflow: clip)
        this.dialog.scrollLeft = 0;
        this.dialog.scrollTop = 0;
      }
      if (!this.active) {
        this.active = true;
        document.documentElement.classList.add('aoa-search-lock');
        this.setInert(true);
        this.setExpanded(true);
      }
      // In the same user gesture: iOS only raises the keyboard for a synchronous focus
      this.input.focus({ preventScroll: true });
      // Two frames: the closed position is painted first, then the panel slides in
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.isOpen) this.dialog.setAttribute('data-state', 'open');
      }));
      emit('search:open');
    }

    close(options = {}) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.restoreFocus = options.restoreFocus !== false;
      this.dialog.removeAttribute('data-state');
      clearTimeout(this.closeTimer);
      const wait = options.immediate || reducedMotion.matches ? 0 : transitionTime(this.panel);
      if (!wait) {
        this.finish();
        return;
      }
      this.closeTimer = setTimeout(() => this.finish(), wait + 20);
    }

    finish() {
      clearTimeout(this.closeTimer);
      if (this.isOpen || !this.active) return;
      this.active = false;
      if (this.dialog.open) this.dialog.close();
      this.setInert(false);
      document.documentElement.classList.remove('aoa-search-lock');
      this.setExpanded(false);
      if (this.restoreFocus) {
        const visible = (el) => el && el.isConnected && el.getClientRects().length > 0;
        const target = visible(this.opener) ? this.opener : Array.from(document.querySelectorAll(TRIGGERS)).find(visible);
        if (target) target.focus({ preventScroll: true });
      }
      this.opener = null;
      emit('search:close');
    }

    onDismiss = (event) => {
      event.preventDefault();
      this.close();
    };

    // Esc (and the Android back gesture): animate instead of the instant native close
    onCancel = (event) => {
      event.preventDefault();
      this.close();
    };

    // The dialog closed by other means (a cancel that could not be prevented, form method=dialog)
    onNativeClose = () => {
      if (this.dialog.open || !this.active) return;
      this.isOpen = false;
      this.restoreFocus = true;
      this.finish();
    };

    onCommand = (event) => {
      if (event.command === 'show-modal') {
        event.preventDefault();
        this.open(event.source);
      } else if (event.command === 'close' || event.command === 'request-close') {
        event.preventDefault();
        this.close();
      }
    };

    onHandoff = (event) => {
      const quick = event.target.closest && event.target.closest('quick-view');
      if (!quick || !this.panel.contains(quick)) return;
      this.close({ immediate: true, restoreFocus: false });
      // The theme opens its product drawer but its close control cannot take focus: move focus into the panel
      requestAnimationFrame(() => {
        const panel = document.querySelector('.side-panel.active[tabindex]');
        if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
      });
    };

    /* ---------- focus ---------- */

    focusables() {
      return Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.tabIndex >= 0 && !el.closest('[inert], [hidden]') && el.getClientRects().length > 0
      );
    }

    onKeydown = (event) => {
      if (event.key !== 'Tab' || !this.isOpen) return;
      const items = this.focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      if (event.shiftKey && (current === first || !this.panel.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !this.panel.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };

    // Mobile row: a card reached with the keyboard slides into view (a tap does not move it)
    onFocusin = (event) => {
      if (!mobile.matches) return;
      const target = event.target;
      if (!target.matches || !target.matches(':focus-visible')) return;
      const item = target.closest('.aoa-search__item');
      if (item) item.scrollIntoView({ inline: 'start', block: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    // Everything outside the drawer, walking up to <body>; elements already inert are left alone
    setInert(on) {
      if (!on) {
        this.inerted.forEach((el) => {
          el.inert = false;
        });
        this.inerted = [];
        return;
      }
      if (this.inerted.length) return;
      let node = this;
      while (node && node !== document.body && node.parentElement) {
        Array.from(node.parentElement.children).forEach((sibling) => {
          if (sibling === node || SKIP_INERT.test(sibling.tagName) || sibling.inert) return;
          sibling.inert = true;
          this.inerted.push(sibling);
        });
        node = node.parentElement;
      }
    }

    setExpanded(value) {
      document.querySelectorAll(TRIGGERS).forEach((trigger) => trigger.setAttribute('aria-expanded', String(value)));
    }

    syncTriggers = () => {
      document.querySelectorAll(TRIGGERS).forEach((trigger) => {
        trigger.setAttribute('aria-controls', this.dialog.id);
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', String(this.isOpen));
      });
    };

    /* ---------- predictive search ---------- */

    onSubmit = (event) => {
      if (!this.input.value.trim()) event.preventDefault();
    };

    onInput = debounce(() => this.search(), 300);

    search() {
      const query = this.input.value.trim();
      if (this.controller) this.controller.abort();
      if (!query) {
        this.showResults(false);
        this.results.removeAttribute('aria-busy');
        return;
      }
      const url = new URL(this.dataset.predictiveUrl || '', window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,article,query');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][fields]', 'title,product_type,vendor,variants.title,variants.sku');
      url.searchParams.set('section_id', 'predictive-search');

      this.controller = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      fetch(url, { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.text();
        })
        .then((text) => {
          if (query !== this.input.value.trim()) return;
          const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
          this.render(section ? section.innerHTML : '');
        })
        .catch((error) => {
          if (error.name !== 'AbortError') this.results.removeAttribute('aria-busy');
        });
    }

    render(markup) {
      this.results.removeAttribute('aria-busy');
      this.results.innerHTML = markup;
      // Query suggestions come with tabindex="-1": keep them reachable with the keyboard
      this.results.querySelectorAll('a[tabindex="-1"]').forEach((link) => link.removeAttribute('tabindex'));
      // "View all results": a real submit button of the drawer form, styled as the AoA button
      const all = this.results.querySelector('#search-results-submit');
      if (all && this.form) {
        if (!this.form.id) this.form.id = this.dialog.id + '-Form';
        all.type = 'submit';
        all.setAttribute('form', this.form.id);
        all.className = 'aoa-btn aoa-search__all';
      }
      this.showResults(markup.trim() !== '');
    }

    showResults(on) {
      this.results.hidden = !on;
      if (this.initial) this.initial.hidden = on;
    }
  }

  customElements.define('aoa-search-drawer', AoaSearchDrawer);
}
